'use client'

import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { Project } from '@/lib/types'
import { firebaseService } from '@/lib/firebase'
import { useAuth } from './AuthContext'

interface ProjectsContextType {
  projects: Project[]
  loading: boolean
  createProject: (projectData: Omit<Project, 'id' | 'createdAt' | 'updatedAt' | 'userId'>) => Promise<Project>
  updateProject: (projectId: string, updates: Partial<Omit<Project, 'id' | 'createdAt' | 'userId'>>) => Promise<Project>
  deleteProject: (projectId: string) => Promise<string>
  markWorkedOn: (projectId: string) => Promise<void>
  recordAppOpen: (userId: string) => Promise<void>
  error: string | null
  clearError: () => void
}

const ProjectsContext = createContext<ProjectsContextType | undefined>(undefined)

export function ProjectsProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const [projects, setProjects] = useState<Project[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!user) {
      setProjects([])
      setLoading(false)
      return
    }

    setLoading(true)
    
    // Subscribe to user's projects
    const unsubscribe = firebaseService.subscribeToProjects(
      user.uid, 
      (userProjects) => {
        setProjects(userProjects)
        setLoading(false)
      },
      (error) => {
        console.error('Error subscribing to projects:', error)
        const errorMessage = error.message || 'Failed to load projects'
        setError(errorMessage)
        setLoading(false)
        setProjects([])
      }
    )

    return unsubscribe
  }, [user])

  const createProject = async (projectData: Omit<Project, 'id' | 'createdAt' | 'updatedAt' | 'userId'>) => {
    try {
      setError(null)
      const firebaseProject = await firebaseService.createProject(projectData)
      // Convert Firebase Timestamp to Date objects
      const newProject: Project = {
        ...firebaseProject,
        createdAt: firebaseProject.createdAt.toDate(),
        updatedAt: firebaseProject.updatedAt.toDate(),
      }
      return newProject
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create project'
      setError(errorMessage)
      throw err
    }
  }

  const updateProject = async (projectId: string, updates: Partial<Omit<Project, 'id' | 'createdAt' | 'userId'>>) => {
    try {
      setError(null)
      await firebaseService.updateProject(projectId, updates)
      
      // Find the existing project to merge with updates
      const existingProject = projects.find(p => p.id === projectId)
      if (!existingProject) {
        throw new Error('Project not found')
      }
      
      // Create updated project with merged data
      const updatedProject: Project = {
        ...existingProject,
        ...updates,
        updatedAt: new Date(),
      }
      
      return updatedProject
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update project'
      setError(errorMessage)
      throw err
    }
  }

  const deleteProject = async (projectId: string) => {
    try {
      setError(null)
      const deletedId = await firebaseService.deleteProject(projectId)
      return deletedId
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete project'
      setError(errorMessage)
      throw err
    }
  }

  /**
   * Mark real work on a project — the decay clock. No local state merge is
   * needed: the onSnapshot subscription pushes the new lastWorkedAt back and
   * the list re-ranks itself.
   */
  const markWorkedOn = async (projectId: string) => {
    try {
      setError(null)
      await firebaseService.markWorkedOn(projectId)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to record work'
      setError(errorMessage)
      throw err
    }
  }

  const recordAppOpen = async (userId: string) => {
    // Deliberately not surfaced as an error — see firebaseService.recordAppOpen.
    await firebaseService.recordAppOpen(userId)
  }

  const clearError = () => setError(null)

  const value = {
    projects,
    loading,
    createProject,
    updateProject,
    deleteProject,
    markWorkedOn,
    recordAppOpen,
    error,
    clearError
  }

  return (
    <ProjectsContext.Provider value={value}>
      {children}
    </ProjectsContext.Provider>
  )
}

export function useProjects() {
  const context = useContext(ProjectsContext)
  if (context === undefined) {
    throw new Error('useProjects must be used within a ProjectsProvider')
  }
  return context
}
